import type { IFileMeta } from './types';

const trimmed = (value?: string | null) =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : '';

const IMAGE_VARIANT_WIDTHS = [540, 1080] as const;

/** Variant keys are width caps for the aspect-preserving rendition. */
export const getMessageImageVariantPixelWidth = (image: IFileMeta, maxDimension: number) => {
  const sourceWidth = Number.isFinite(image.width) && image.width! > 0 ? image.width! : undefined;
  return sourceWidth ? Math.floor(Math.min(sourceWidth, maxDimension)) : maxDimension;
};

export const getReadyMessageImageVariants = (image: IFileMeta) => {
  if (
    image?.processing_state === 'processing' ||
    image?.processing_state === 'failed' ||
    image.mime_type === 'image/gif'
  )
    return [];

  return IMAGE_VARIANT_WIDTHS.flatMap((width) => {
    const uri = trimmed(image.variants?.[String(width) as '540' | '1080']);
    return uri ? [{ width, uri }] : [];
  });
};

export const getMessageImageSrcSetCandidates = (image: IFileMeta) => {
  const sourceWidths = new Map<number, string>();

  for (const variant of getReadyMessageImageVariants(image)) {
    const width = getMessageImageVariantPixelWidth(image, variant.width);
    if (!sourceWidths.has(width)) sourceWidths.set(width, variant.uri);
  }

  return Array.from(sourceWidths, ([width, uri]) => ({ width: Math.floor(width), uri }));
};

export const resolveMessageImageSrcSet = (image: IFileMeta) =>
  getMessageImageSrcSetCandidates(image)
    .map(({ width, uri }) => `${uri} ${width}w`)
    .join(', ');

/** Contained image widths in layout points, matching the native grid heights. */
export const getMessageImageTargetWidths = (
  images: IFileMeta[],
  containerWidth: number,
  viewportWidth = containerWidth,
  layoutRem = 14,
) => {
  const width = Math.max(0, containerWidth);
  const displayedCount = Math.min(images.length, 4);
  const large = viewportWidth >= 1024;
  return images.map((image, index) => {
    let fraction = 1;
    let height = large ? 320 : 192;
    if (displayedCount === 2) {
      fraction = 0.5;
      height = large ? 192 : 128;
    } else if (displayedCount >= 3 && index > 0) {
      fraction = displayedCount === 3 ? 0.5 : 1 / 3;
      height = large ? 160 : 96;
    }

    const tileWidth = Math.max(0, width * fraction - 1.4);
    const tileHeight = (height / 16) * layoutRem - 1.4;
    const aspect =
      Number.isFinite(image.width) &&
      image.width! > 0 &&
      Number.isFinite(image.height) &&
      image.height! > 0
        ? image.width! / image.height!
        : undefined;
    return aspect ? Math.min(tileWidth, tileHeight * aspect) : tileWidth;
  });
};

const resolveMessageImageVariant = (
  image: IFileMeta,
  renderedWidth: number | undefined,
  pixelRatio: number,
) => {
  const variants = getReadyMessageImageVariants(image);
  if (variants.length === 0) return '';

  const measuredWidth = Number.isFinite(renderedWidth) && renderedWidth! > 0;
  if (!measuredWidth) return '';

  const requestedPixels = Math.ceil(renderedWidth! * Math.max(1, pixelRatio));
  const sourceWidth = Number.isFinite(image.width) && image.width! > 0 ? image.width! : undefined;
  const targetPixels = sourceWidth ? Math.min(requestedPixels, sourceWidth) : requestedPixels;
  return (
    (
      variants.find(
        (variant) => getMessageImageVariantPixelWidth(image, variant.width) >= targetPixels,
      ) || variants[variants.length - 1]
    )?.uri || ''
  );
};

/**
 * Uploads used to key S3 objects by `<timestamp>-<filename>`, and every image in
 * a message was uploaded as `image.jpg`. Images processed in the same
 * millisecond therefore collapsed onto one processed object, so a message can
 * hold several entries sharing a single `source`. The per-image originals are
 * still distinct, so prefer those whenever a `source` is not unique.
 */
export const resolveMessageImageUrls = (
  images: IFileMeta[],
  options?: {
    /** Measured layout width in points, one entry per image. */
    renderedWidths?: number[];
    pixelRatio?: number;
  },
): string[] => {
  const sourceCounts = new Map<string, number>();
  images.forEach((img) => {
    const source = trimmed(img?.source);
    if (source) sourceCounts.set(source, (sourceCounts.get(source) || 0) + 1);
  });

  return images.map((img, index) => {
    const source = trimmed(img?.source);
    const original = trimmed(img?.original_source);
    const processed = trimmed(img?.processed_source);

    if (img?.processing_state === 'processing' && original) return original;
    if (source && (sourceCounts.get(source) || 0) > 1 && original) return original;

    const variantUrl = options
      ? resolveMessageImageVariant(img, options.renderedWidths?.[index], options.pixelRatio ?? 1)
      : '';

    return variantUrl || source || original || processed || '';
  });
};

/** Full-screen viewers keep the original resolution when it is available. */
export const resolveMessageImageViewerUrls = (images: IFileMeta[]): string[] =>
  images.map((image) => {
    const original = trimmed(image?.original_source);
    const source = trimmed(image?.source);
    const processed = trimmed(image?.processed_source);
    return original || source || processed || '';
  });

export const resolveMessageImageFallbackUrl = (
  img: IFileMeta,
  activeUri: string,
): string | undefined =>
  [img?.original_source, img?.processed_source, img?.source]
    .map(trimmed)
    .find((candidate) => candidate && candidate !== activeUri) || undefined;
